import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import CircleLoader from 'react/components/CircleLoader';
import MaterialIcon from 'react/elements/MaterialIcon';
import { moneyFormatFromCents } from 'utilities/moneyFormat';
import {
	addBankRecordToBudget,
	fetchUnbudgeted,
	setBankRecordHidden,
	unbudgetedShows,
	unbudgetedSources,
} from 'redux/actions/unbudgeted';

const SOURCE_LABELS = [
	[unbudgetedSources.IMPORTED, 'Imported'],
	[unbudgetedSources.PLAID, 'Plaid'],
	[unbudgetedSources.ALL, 'Both'],
];

const SHOW_LABELS = [
	[unbudgetedShows.REVIEW, 'Needs a look'],
	[unbudgetedShows.VISIBLE, 'All'],
	[unbudgetedShows.HIDDEN, 'Hidden'],
];

/**
 * The transactions page's Unbudgeted tab: bank records no budget transaction is linked to, which
 * count as neither spending nor income. An import leaves transfers and card payments this way on
 * purpose, and a rule that guessed wrong leaves real income or spending the same way, so they are
 * worth checking. "Needs a look" lists only those with no matching transfer on another account
 * (account/unbudgeted), which is where the mistakes are.
 *
 * Each one can be added to the budget, which opens the edit popup on a transaction made from it and
 * links the two when saved, or hidden, which keeps it off this tab and the month's bank activity.
 * Imported records come first; the old Plaid ones, thousands of them, are a filter away.
 */
export default function UnbudgetedBankRecords() {
	const dispatch = useDispatch();
	// Bumped by every saved transaction, so a record added to the budget leaves the list.
	const transactionFinderUpdates = useSelector((state) => state.dashboard.transactionFinderUpdates);
	const [source, setSource] = useState(unbudgetedSources.IMPORTED);
	const [show, setShow] = useState(unbudgetedShows.REVIEW);
	const [page, setPage] = useState(1);
	const [result, setResult] = useState(null);
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(null);
	const [error, setError] = useState(null);
	const [notice, setNotice] = useState(null);
	const [reloads, setReloads] = useState(0);
	const latest = useRef(0);

	useEffect(() => {
		const request = ++latest.current;
		setLoading(true);
		fetchUnbudgeted({ source, show, page })
			.then((answer) => {
				if(request !== latest.current) {
					return;
				}
				// A hide can empty the last page; step back to one that has rows.
				if(answer.transactions.length === 0 && page > 1) {
					setPage(Math.max(1, Math.ceil(answer.total / answer.per_page)));
					return;
				}
				setResult(answer);
				setLoading(false);
				setError(null);
			})
			.catch(() => {
				if(request === latest.current) {
					setLoading(false);
					setError('The bank records did not load. Try again.');
				}
			});
	}, [source, show, page, reloads, transactionFinderUpdates]);

	useEffect(() => () => {
		latest.current = -1;
	}, []);

	const choose = (setter) => (value) => {
		setter(value);
		setPage(1);
		setNotice(null);
	};

	const hide = (record, hidden) => {
		setBusy(record.id);
		setError(null);
		setNotice(null);
		setBankRecordHidden(record, hidden)
			.then(() => {
				setBusy(null);
				setReloads((count) => count + 1);
				setNotice({
					text: hidden ? `Hid ${record.name || 'that record'}.` : `${record.name || 'That record'} is shown again.`,
					undo: hidden ? () => hide(record, false) : null,
				});
			})
			.catch(() => {
				setBusy(null);
				setError(hidden ? 'That could not be hidden. Try again.' : 'That could not be shown again. Try again.');
			});
	};

	const counts = result ? result.counts : null;
	const first = result && result.total ? (result.page - 1) * result.per_page + 1 : 0;
	const last = result ? first + result.transactions.length - (result.transactions.length ? 1 : 0) : 0;
	const pages = result ? Math.max(1, Math.ceil(result.total / result.per_page)) : 1;

	return (
		<div className='unbudgeted_records'>
			<p className='unbudgeted_records__intro'>
				Bank records with no transaction in the budget. They count as neither spending nor income.
				Transfers and card payments belong here; anything else should be added to the budget.
			</p>

			<div className='transactions_filters' role='group' aria-label='Where from'>
				{SOURCE_LABELS.map(([value, label]) =>
				<button key={value} type='button' className='btn-flat transactions_filter' aria-pressed={source === value} onClick={() => choose(setSource)(value)}>
					{label}
				</button>
				)}
			</div>
			<div className='transactions_filters' role='group' aria-label='Which'>
				{SHOW_LABELS.map(([value, label]) =>
				<button key={value} type='button' className='btn-flat transactions_filter' aria-pressed={show === value} onClick={() => choose(setShow)(value)}>
					{label}
					{!!counts && <span className='unbudgeted_records__count'>{counts[value]}</span>}
				</button>
				)}
			</div>
			{show === unbudgetedShows.REVIEW &&
			<p className='unbudgeted_records__hint'>
				No matching amount the other way on another of your accounts within a few days, so not a plain transfer.
			</p>
			}

			{!!error && <p className='unbudgeted_records__error red-text'>{error}</p>}
			{!!notice &&
			<p className='unbudgeted_records__notice'>
				{notice.text}
				{!!notice.undo &&
				<button type='button' className='btn-flat unbudgeted_records__undo' disabled={busy !== null} onClick={notice.undo}>
					Undo
				</button>
				}
			</p>
			}

			{loading && !result && <CircleLoader />}
			{!!result && result.transactions.length === 0 &&
			<p className='unbudgeted_records__empty'>
				{show === unbudgetedShows.REVIEW ? 'Nothing needs a look.' : 'Nothing here.'}
			</p>
			}
			{!!result && result.transactions.length > 0 &&
			<ul className={`unbudgeted_records__list${loading ? ' is-loading' : ''}`}>
				{result.transactions.map((record) =>
				<UnbudgetedRecord
					key={record.id}
					record={record}
					busy={busy === record.id}
					onAdd={() => dispatch(addBankRecordToBudget(record))}
					onHide={(hidden) => hide(record, hidden)}
				/>
				)}
			</ul>
			}

			{!!result && result.total > result.per_page &&
			<div className='unbudgeted_records__pager'>
				<button type='button' className='btn-flat unbudgeted_records__page' disabled={page <= 1 || loading} onClick={() => setPage(page - 1)} aria-label='Newer'>
					<MaterialIcon>chevron_left</MaterialIcon>
				</button>
				<span className='unbudgeted_records__range'>{first}–{last} of {result.total}</span>
				<button type='button' className='btn-flat unbudgeted_records__page' disabled={page >= pages || loading} onClick={() => setPage(page + 1)} aria-label='Older'>
					<MaterialIcon>chevron_right</MaterialIcon>
				</button>
			</div>
			}
		</div>
	);
}

/** One record: what the bank said, which account, whether it has another side, and what to do with it. */
function UnbudgetedRecord({ record, busy, onAdd, onHide }) {
	const statement = record.original_statement && record.original_statement !== record.name ? record.original_statement : null;
	const moneyIn = record.amount > 0;

	return (
		<li className={`unbudgeted_records__record${busy ? ' is-busy' : ''}`}>
			<div className='unbudgeted_records__main'>
				<span className='unbudgeted_records__name'>{record.name || 'No name'}</span>
				<span className='unbudgeted_records__muted'>
					{moment(record.date, 'YYYY-MM-DD').format('ddd, MMM D, YYYY')} · {record.account.name}
					{record.source === 'plaid' && ' · Plaid'}
					{record.pending && ' · pending'}
				</span>
				{!!statement && <span className='unbudgeted_records__muted unbudgeted_records__statement'>{statement}</span>}
				<span className='unbudgeted_records__badges'>
					{record.other_side
						? <span className='unbudgeted_records__badge'>
							Transfer with {record.other_side.account_name}, {moment(record.other_side.date, 'YYYY-MM-DD').format('MMM D')}
						</span>
						: <span className='unbudgeted_records__badge is-alone'>No other side</span>
					}
					{record.hidden_by_account && <span className='unbudgeted_records__badge'>Its account hides its transactions</span>}
				</span>
			</div>
			<div className='unbudgeted_records__side'>
				<strong className={`unbudgeted_records__amount${moneyIn ? ' is-in' : ''}`}>
					{moneyIn ? '+' : '−'}{moneyFormatFromCents(Math.abs(record.amount))}
				</strong>
				<div className='unbudgeted_records__actions'>
					<button type='button' className='btn-flat unbudgeted_records__add' disabled={busy} onClick={onAdd}>
						<MaterialIcon>playlist_add</MaterialIcon>
						Add to budget
					</button>
					{!record.hidden &&
					<button type='button' className='btn-flat unbudgeted_records__hide' disabled={busy} onClick={() => onHide(true)}>
						<MaterialIcon>visibility_off</MaterialIcon>
						Hide
					</button>
					}
					{record.hidden && !record.hidden_by_account &&
					<button type='button' className='btn-flat unbudgeted_records__show' disabled={busy} onClick={() => onHide(false)}>
						<MaterialIcon>visibility</MaterialIcon>
						Show
					</button>
					}
				</div>
			</div>
		</li>
	);
}
